import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Title } from '@angular/platform-browser';

import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { Toast } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

import { NavigationContextService } from '../../../core/services/navigation-context.service';
import { RegistryTableComponent } from '../../../core/registry-table/registry-table.component';
import { escapeHtml } from '../../../core/validators/no-special-chars.validator';
import { decodeAssetType, decodeSubDomain } from '../../models/document-asset-labels';
import {
  DocumentAssetStatus,
  DocumentAssetSummary,
  DocumentAssetType,
} from '../../models/document-asset.model';
import { DocumentAttachmentV2Service } from '../../services/document-attachment-v2.service';
import { ColumnConfig } from '../dashboard/table-config';
import {
  DocumentAssetRegisteredEvent,
  DocumentAssetUploadWizardComponent,
} from '../document-asset-upload-wizard/document-asset-upload-wizard.component';

interface AttachmentRow {
  assetId: number;
  id: number;
  businessKey: string;
  name: string;
  assetType: DocumentAssetType | string;
  subDomainType: string;
  versionLabel: string;
  status: DocumentAssetStatus | string;
  lastModified: string;
  originalFileName: string;
  raw: DocumentAssetSummary;
}

@Component({
  selector: 'app-document-attachment-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RegistryTableComponent,
    Toast,
    BreadcrumbModule,
    ButtonModule,
    ConfirmDialogModule,
    ToggleSwitchModule,
    DocumentAssetUploadWizardComponent,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './document-attachment-list.component.html',
  styleUrls: ['./document-attachment-list.component.scss'],
})
export class DocumentAttachmentListComponent implements OnInit {
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly attachmentService = inject(DocumentAttachmentV2Service);
  private readonly navigationContext = inject(NavigationContextService);
  private readonly titleService = inject(Title);

  readonly attachmentsData = signal<AttachmentRow[]>([]);
  readonly isLoading = signal<boolean>(false);
  readonly breadcrumbItems = signal<MenuItem[]>([]);
  readonly wizardVisible = signal<boolean>(false);
  readonly wizardMode = signal<'create' | 'edit'>('create');
  readonly editingAsset = signal<DocumentAssetSummary | null>(null);
  readonly showInactive = signal<boolean>(false);
  readonly emptyMessage = signal<string>(
    'Nessun allegato riusabile registrato. Carica il tuo primo allegato PDF o DOCX.',
  );

  readonly columns: ColumnConfig[] = [
    { field: 'name', title: 'Nome Allegato', type: 'text', sortable: true, filterType: 'text', filterPlaceholder: 'Cerca nome' },
    {
      field: 'assetType',
      title: 'Tipologia',
      type: 'tag',
      sortable: true,
      filterType: 'text',
      filterPlaceholder: 'Cerca tipologia',
    },
    { field: 'subDomainType', title: 'Categoria', type: 'text', sortable: true, filterType: 'text', filterPlaceholder: 'Cerca categoria' },
    { field: 'versionLabel', title: 'Versione', type: 'text', sortable: true, filterType: 'text', filterPlaceholder: 'Cerca versione' },
    {
      field: 'status',
      title: 'Stato',
      type: 'tag',
      sortable: true,
      filterType: 'select',
      filterPlaceholder: 'Filtra stato',
      filterOptions: [
        { label: 'Bozza', value: 'DRAFT' },
        { label: 'Attivo', value: 'ACTIVE' },
        { label: 'Ritirato', value: 'RETIRED' },
      ],
    },
    { field: 'lastModified', title: 'Ultima Modifica', type: 'text', sortable: true, filterType: 'date' },
    { title: 'Azioni', type: 'action', filterType: 'none' },
  ];

  ngOnInit(): void {
    this.titleService.setTitle('Allegati Riusabili - PalEasy');
    this.breadcrumbItems.set([{ label: 'Allegati Riusabili' }]);
    this.loadAttachments();
  }

  loadAttachments(): void {
    this.isLoading.set(true);
    const domainType = this.currentDomainType();
    this.attachmentService.list({ domainType, includeInactive: this.showInactive() }).subscribe({
      next: (response) => {
        const items = response?.data ?? [];
        this.attachmentsData.set(items.map((item) => this.toRow(item)));
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Error loading V2 attachments:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare gli allegati riusabili',
        });
        this.attachmentsData.set([]);
        this.isLoading.set(false);
      },
    });
  }

  createNewAttachment(): void {
    this.wizardMode.set('create');
    this.editingAsset.set(null);
    this.wizardVisible.set(true);
  }

  onToggleShowInactive(value: boolean): void {
    this.showInactive.set(value);
    this.loadAttachments();
  }

  onEditClick(row: AttachmentRow): void {
    this.wizardMode.set('edit');
    this.editingAsset.set(row.raw);
    this.wizardVisible.set(true);
  }

  onWizardRegistered(event: DocumentAssetRegisteredEvent): void {
    this.loadAttachments();
    this.messageService.add({
      severity: 'success',
      summary: 'Allegato registrato',
      detail: `${event.businessKey} v${event.version} \u2014 stato ${event.status.toLowerCase()}.`,
      life: 4000,
    });
  }

  onDownloadClick(row: AttachmentRow): void {
    this.attachmentService.download(row.assetId).subscribe({
      next: (blob) => this.triggerDownload(blob, row.originalFileName || `${row.businessKey}.bin`),
      error: (err) => {
        console.error('Error downloading attachment:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile scaricare l\u2019allegato',
        });
      },
    });
  }

  onDeleteClick(row: AttachmentRow): void {
    this.attachmentService.references(row.assetId).subscribe({
      next: (response) => this.confirmDeleteWithReferences(row, response?.data),
      error: (err) => {
        console.error('Error loading references:', err);
        this.confirmDeleteWithReferences(row, undefined);
      },
    });
  }

  onClearFilters(): void {
    this.showInactive.set(false);
    this.loadAttachments();
  }

  onExport(): void {
    this.messageService.add({
      severity: 'info',
      summary: 'Esportazione',
      detail: 'La funzione di esportazione sarà disponibile a breve.',
      life: 3000,
    });
  }

  private confirmDeleteWithReferences(
    row: AttachmentRow,
    references:
      | { activeReferenceCount?: number; retiredReferenceCount?: number }
      | undefined,
  ): void {
    const activeRefs = references?.activeReferenceCount ?? 0;
    const requiresForce = activeRefs > 0;
    const message = requiresForce
      ? `L\u2019allegato <strong>${escapeHtml(row.name || row.businessKey)}</strong> è utilizzato da <strong>${activeRefs}</strong> template attivo/i. Confermando verrà ritirato ma i binding esistenti resteranno intatti nelle versioni già in uso. Procedere?`
      : `Sei sicuro di voler ritirare l\u2019allegato <strong>${escapeHtml(row.name || row.businessKey)}</strong>? L\u2019operazione è reversibile creando una nuova versione.`;

    this.confirmationService.confirm({
      header: requiresForce ? 'Conferma Eliminazione Forzata' : 'Conferma Eliminazione',
      message,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: requiresForce ? 'Sì, ritira comunque' : 'Sì, ritira',
      rejectLabel: 'Annulla',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => this.performDelete(row, requiresForce),
    });
  }

  private performDelete(row: AttachmentRow, force: boolean): void {
    this.attachmentService.delete(row.assetId, force).subscribe({
      next: (response) => {
        if (response?.status) {
          this.messageService.add({
            severity: 'success',
            summary: 'Allegato ritirato',
            detail: `L\u2019allegato "${row.name || row.businessKey}" è stato ritirato correttamente.`,
            life: 3000,
          });
          this.loadAttachments();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: response?.message ?? 'Impossibile ritirare l\u2019allegato',
          });
        }
      },
      error: (err) => {
        console.error('Error deleting attachment:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile ritirare l\u2019allegato',
        });
      },
    });
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private toRow(item: DocumentAssetSummary): AttachmentRow {
    return {
      assetId: item.assetId,
      id: item.assetId,
      businessKey: item.businessKey,
      name: item.name ?? '',
      assetType: decodeAssetType(item.assetType ?? 'ATTACHMENT'),
      subDomainType: decodeSubDomain(item.subDomainType),
      versionLabel: `v${item.version}`,
      status: item.status ?? 'DRAFT',
      lastModified: this.formatDate(item.updatedAt ?? item.createdAt),
      originalFileName: item.originalFileName ?? '',
      raw: item,
    };
  }

  private formatDate(value?: string): string {
    if (!value) return '-';
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? '-' : parsed.toLocaleDateString('it-IT');
  }

  currentDomainType(): 'SUPPLIERS' | 'PROFESSIONALS' {
    return this.navigationContext.isSupplierContext() ? 'SUPPLIERS' : 'PROFESSIONALS';
  }

  private domainPrefix(): string {
    return this.navigationContext.getDomainPrefix();
  }
}
